/**
 * GoalsPage — Sinking funds / savings goals management
 *
 * Sub-modes: list | add | edit
 *
 * Features:
 *   - List all active goals with progress bars and color-coded pace
 *   - Add new goals with emoji picker, target amount, monthly contribution
 *   - Edit existing goals (name, amounts, target date, emoji)
 *   - Quick contribute button (inline amount input)
 *   - Swipe-to-delete with confirmation dialog
 *   - Summary bar with total saved / total target
 *   - Months remaining estimate when monthly_contribution > 0
 *
 * Phase 9E: Sinking Funds
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  getGoals, addGoal, updateGoal, deleteGoal, contributeToGoal
} from '../services/db';
import { formatCurrency } from '../utils/format';
import { SkeletonLine, SkeletonCard } from '../components/Skeleton';
import theme from '../utils/theme';

const EMOJI_OPTIONS = ['🎯', '🏖️', '🎄', '🏠', '🚗', '💊', '🎓', '💍', '👶', '🛡️', '💰', '✈️'];

/**
 * Calculate progress color class based on pace vs target date.
 * @param {Object} goal - Goal object
 * @returns {string} CSS modifier class
 */
function getProgressColor(goal) {
  const pct = goal.target_amount > 0
    ? (goal.current_amount / goal.target_amount) * 100
    : 0;

  if (pct >= 100) return 'goal-progress-fill--complete';

  if (!goal.target_date) return 'goal-progress-fill--on-track';

  const now = new Date();
  const created = goal.created_at?.toDate
    ? goal.created_at.toDate()
    : goal.created_at
      ? new Date(goal.created_at)
      : now;
  const target = new Date(goal.target_date + 'T00:00:00');

  const totalDays = Math.max(1, (target - created) / (1000 * 60 * 60 * 24));
  const elapsedDays = Math.max(0, (now - created) / (1000 * 60 * 60 * 24));
  const expectedPct = Math.min(100, (elapsedDays / totalDays) * 100);

  if (expectedPct === 0) return 'goal-progress-fill--on-track';

  const ratio = pct / expectedPct;
  if (ratio >= 0.8) return 'goal-progress-fill--on-track';
  if (ratio >= 0.5) return 'goal-progress-fill--behind';
  return 'goal-progress-fill--way-behind';
}

export default function GoalsPage() {
  const { user } = useAuth();
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('list'); // list | add | edit
  const [editingGoal, setEditingGoal] = useState(null);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [contributingId, setContributingId] = useState(null);
  const [contributeAmount, setContributeAmount] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    name: '', target_amount: '', current_amount: '', monthly_contribution: '',
    emoji: '🎯', target_date: '',
  });

  const loadGoals = useCallback(async () => {
    try {
      const data = await getGoals(user.id);
      setGoals(data.filter(g => g.active !== false));
    } catch (err) {
      console.error('Failed to load goals:', err);
      setError(err.message || 'Failed to load goals');
    } finally {
      setLoading(false);
    }
  }, [user.id]);

  useEffect(() => { loadGoals(); }, [loadGoals]);

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 3000);
  };

  const updateForm = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAdd = () => {
    setFormData({
      name: '', target_amount: '', current_amount: '', monthly_contribution: '',
      emoji: '🎯', target_date: '',
    });
    setEditingGoal(null);
    setMode('add');
  };

  const handleEdit = (goal) => {
    setFormData({
      name: goal.name || '',
      target_amount: goal.target_amount || '',
      current_amount: goal.current_amount || '',
      monthly_contribution: goal.monthly_contribution || '',
      emoji: goal.emoji || '🎯',
      target_date: goal.target_date || '',
    });
    setEditingGoal(goal);
    setMode('edit');
  };

  const handleSave = async () => {
    if (!formData.name || !formData.target_amount) {
      showMessage('Name and target amount are required.', 'error');
      return;
    }

    const goalData = {
      name: formData.name,
      target_amount: Number(formData.target_amount),
      monthly_contribution: Number(formData.monthly_contribution) || 0,
      emoji: formData.emoji,
      target_date: formData.target_date || null,
    };

    try {
      if (mode === 'edit' && editingGoal) {
        await updateGoal(user.id, editingGoal.id, goalData);
        showMessage('Goal updated!', 'success');
      } else {
        goalData.current_amount = Number(formData.current_amount) || 0;
        await addGoal(user.id, goalData);
        showMessage('Goal created!', 'success');
      }
      setMode('list');
      await loadGoals();
    } catch (err) {
      showMessage('Failed to save: ' + err.message, 'error');
    }
  };

  const handleDelete = async (goal) => {
    if (!window.confirm(`Delete "${goal.name}"? This cannot be undone.`)) return;
    try {
      await deleteGoal(user.id, goal.id);
      showMessage('Goal deleted.', 'success');
      setMode('list');
      await loadGoals();
    } catch (err) {
      showMessage('Failed to delete: ' + err.message, 'error');
    }
  };

  const handleContribute = async (goal) => {
    const amount = Number(contributeAmount);
    if (!amount || amount <= 0) {
      showMessage('Enter a positive amount.', 'error');
      return;
    }
    try {
      await contributeToGoal(user.id, goal.id, amount);
      setContributingId(null);
      setContributeAmount('');
      showMessage(`Added ${formatCurrency(amount)} to ${goal.name}!`, 'success');
      await loadGoals();
    } catch (err) {
      showMessage('Failed to contribute: ' + err.message, 'error');
    }
  };

  // Summary calculations
  const totalSaved = goals.reduce((s, g) => s + (g.current_amount || 0), 0);
  const totalTarget = goals.reduce((s, g) => s + (g.target_amount || 0), 0);
  const overallPct = totalTarget > 0 ? Math.min(100, (totalSaved / totalTarget) * 100) : 0;

  if (loading) return (
    <div>
      <SkeletonCard>
        <SkeletonLine className="skeleton-line--tall" />
        <SkeletonLine className="skeleton-line--short" />
      </SkeletonCard>
      <SkeletonCard>
        {[1, 2, 3].map(i => (
          <div key={i} className="skeleton-row">
            <SkeletonLine className="skeleton-line--medium" />
            <SkeletonLine className="skeleton-line--short" />
          </div>
        ))}
      </SkeletonCard>
    </div>
  );

  if (error) return (
    <div className="card">
      <div className="alert alert-error">{error}</div>
      <button className="btn btn-primary" onClick={() => { setError(null); setLoading(true); loadGoals(); }}>
        Retry
      </button>
    </div>
  );

  // Add/Edit form
  if (mode === 'add' || mode === 'edit') {
    return (
      <div>
        <div className="card">
          <h2>{mode === 'edit' ? 'Edit Goal' : 'New Goal'}</h2>

          <div className="form-group">
            <label>Goal Name</label>
            <input value={formData.name} onChange={e => updateForm('name', e.target.value)} placeholder="e.g., Emergency Fund" />
          </div>

          <div className="form-group">
            <label>Target Amount ($)</label>
            <input type="number" value={formData.target_amount} onChange={e => updateForm('target_amount', e.target.value)} min="0" step="100" />
          </div>

          {mode === 'add' && (
            <div className="form-group">
              <label>Starting Balance ($)</label>
              <input type="number" value={formData.current_amount} onChange={e => updateForm('current_amount', e.target.value)} min="0" step="1" placeholder="0" />
            </div>
          )}

          <div className="form-group">
            <label>Monthly Contribution ($)</label>
            <input type="number" value={formData.monthly_contribution} onChange={e => updateForm('monthly_contribution', e.target.value)} min="0" step="10" placeholder="0" />
          </div>

          <div className="form-group">
            <label>Target Date (optional)</label>
            <input type="date" value={formData.target_date} onChange={e => updateForm('target_date', e.target.value)} />
          </div>

          <div className="form-group">
            <label>Icon</label>
            <div className="emoji-picker">
              {EMOJI_OPTIONS.map(emoji => (
                <button
                  key={emoji}
                  className={`emoji-option ${formData.emoji === emoji ? 'selected' : ''}`}
                  onClick={() => updateForm('emoji', emoji)}
                  type="button"
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>

          <div className="btn-row">
            <button className="btn btn-primary" onClick={handleSave}>
              {mode === 'edit' ? 'Update Goal' : 'Create Goal'}
            </button>
            <button className="btn btn-secondary" onClick={() => setMode('list')}>Cancel</button>
            {mode === 'edit' && editingGoal && (
              <button className="btn btn-danger" onClick={() => handleDelete(editingGoal)}>Delete</button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // List view
  return (
    <div>
      {message && <div className={`alert alert-${message.type}`}>{message.text}</div>}

      {/* Summary bar */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ marginBottom: '0.25rem' }}>Savings Goals</h2>
            <p style={{ fontSize: '0.75rem', color: theme.textMuted }}>
              {goals.length} goal{goals.length !== 1 ? 's' : ''} · {Math.round(overallPct)}% overall
            </p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '1.1rem', fontWeight: 700 }}>{formatCurrency(totalSaved)}</div>
            <div style={{ fontSize: '0.7rem', color: theme.textMuted }}>of {formatCurrency(totalTarget)}</div>
          </div>
        </div>
        <div className="goal-progress-bar" style={{ marginTop: '0.5rem' }}>
          <div
            className={`goal-progress-fill ${overallPct >= 100 ? 'goal-progress-fill--complete' : 'goal-progress-fill--on-track'}`}
            style={{ width: `${overallPct}%` }}
          />
        </div>
      </div>

      {/* Add button */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.5rem' }}>
        <button className="btn btn-success btn-sm" onClick={handleAdd}>+ New Goal</button>
      </div>

      {/* Goal list */}
      <div className="card">
        {goals.length === 0 ? (
          <p className="empty-state">
            No savings goals yet. Create one to start tracking progress!
          </p>
        ) : (
          goals.map(goal => (
            <GoalRow
              key={goal.id}
              goal={goal}
              onEdit={() => handleEdit(goal)}
              onDelete={() => handleDelete(goal)}
              isContributing={contributingId === goal.id}
              contributeAmount={contributingId === goal.id ? contributeAmount : ''}
              onStartContribute={() => { setContributingId(goal.id); setContributeAmount(''); }}
              onCancelContribute={() => { setContributingId(null); setContributeAmount(''); }}
              onContributeAmountChange={setContributeAmount}
              onContribute={() => handleContribute(goal)}
            />
          ))
        )}
      </div>
    </div>
  );
}

/**
 * GoalRow — Individual goal with progress bar, contribute button, and swipe-to-delete.
 */
function GoalRow({
  goal, onEdit, onDelete,
  isContributing, contributeAmount, onStartContribute,
  onCancelContribute, onContributeAmountChange, onContribute,
}) {
  const pct = goal.target_amount > 0
    ? Math.min(100, (goal.current_amount / goal.target_amount) * 100)
    : 0;
  const isComplete = pct >= 100;
  const colorClass = getProgressColor(goal);

  // Months remaining estimate
  let monthsRemaining = null;
  if (!isComplete && goal.monthly_contribution > 0 && goal.target_amount > goal.current_amount) {
    const remaining = goal.target_amount - goal.current_amount;
    monthsRemaining = Math.ceil(remaining / goal.monthly_contribution);
  }

  // Swipe-to-delete refs
  const contentRef = useRef(null);
  const startX = useRef(0);
  const currentX = useRef(0);
  const isOpen = useRef(false);
  const isSwiping = useRef(false);

  const DELETE_WIDTH = 72;
  const THRESHOLD = 60;

  const setTranslate = (x) => {
    if (contentRef.current) {
      contentRef.current.style.transform = `translateX(${x}px)`;
    }
  };

  const handleTouchStart = (e) => {
    startX.current = e.touches[0].clientX;
    isSwiping.current = false;
    if (contentRef.current) contentRef.current.classList.add('swiping');
  };

  const handleTouchMove = (e) => {
    const deltaX = e.touches[0].clientX - startX.current;
    const base = isOpen.current ? -DELETE_WIDTH : 0;
    const newX = Math.min(0, Math.max(-DELETE_WIDTH, base + deltaX));
    currentX.current = newX;
    if (Math.abs(deltaX) > 10) isSwiping.current = true;
    setTranslate(newX);
  };

  const handleTouchEnd = () => {
    if (contentRef.current) contentRef.current.classList.remove('swiping');
    if (currentX.current < -THRESHOLD) {
      setTranslate(-DELETE_WIDTH);
      isOpen.current = true;
    } else {
      setTranslate(0);
      isOpen.current = false;
    }
  };

  const handleContentClick = () => {
    if (isOpen.current) {
      setTranslate(0);
      isOpen.current = false;
    }
  };

  const handleSwipeDelete = () => {
    setTranslate(0);
    isOpen.current = false;
    onDelete();
  };

  return (
    <div className="swipe-container">
      <div className="swipe-delete" onClick={handleSwipeDelete}>🗑️</div>
      <div
        ref={contentRef}
        className="swipe-content"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={handleContentClick}
      >
        <div className="goal-row">
          <span className="goal-emoji">{goal.emoji || '🎯'}</span>

          <div className="goal-info" onClick={() => { if (!isSwiping.current && !isOpen.current) onEdit(); }} style={{ cursor: 'pointer' }}>
            <div className="goal-name">
              {goal.name}
              {isComplete && <span className="goal-complete-badge" style={{ marginLeft: '0.5rem' }}>Complete!</span>}
            </div>
            <div className="goal-progress-bar" style={{ marginTop: '0.35rem' }}>
              <div className={`goal-progress-fill ${colorClass}`} style={{ width: `${pct}%` }} />
            </div>
            <div className="goal-progress-text">
              {formatCurrency(goal.current_amount)} of {formatCurrency(goal.target_amount)} ({Math.round(pct)}%)
            </div>
            <div className="goal-meta">
              {goal.monthly_contribution > 0 && (
                <span>{formatCurrency(goal.monthly_contribution)}/mo</span>
              )}
              {monthsRemaining !== null && (
                <span> · ~{monthsRemaining} mo{monthsRemaining !== 1 ? 's' : ''} to go</span>
              )}
              {goal.target_date && (
                <span> · Target: {new Date(goal.target_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</span>
              )}
            </div>
          </div>

          {!isComplete && (
            <button
              className="goal-contribute-btn"
              onClick={(e) => { e.stopPropagation(); onStartContribute(); }}
            >
              +$
            </button>
          )}
        </div>

        {/* Inline contribute input */}
        {isContributing && (
          <div className="goal-contribute-row">
            <span style={{ fontSize: '0.85rem', color: theme.textMedium }}>$</span>
            <input
              type="number"
              value={contributeAmount}
              onChange={e => onContributeAmountChange(e.target.value)}
              placeholder="0"
              min="0"
              step="1"
              autoFocus
            />
            <button className="btn btn-success btn-sm" onClick={onContribute}>Add</button>
            <button className="btn btn-secondary btn-sm" onClick={onCancelContribute}>Cancel</button>
          </div>
        )}
      </div>
    </div>
  );
}
